/**
 * The one place the access token lives.
 *
 * There used to be three stores — `localStorage.tokens`, `sessionStorage.accessToken`
 * and `sessionStorage.role` — read in a `||` ladder duplicated across three
 * services. That is why Sign Out appeared to do nothing: the header cleared
 * sessionStorage, `localStorage.tokens` survived, and the next reload rehydrated
 * the session the user had just ended.
 *
 * sessionStorage rather than localStorage: the access token dies with the tab.
 * The refresh token is an httpOnly cookie this code cannot read, so a new tab
 * re-derives its session from that cookie (see AuthProvider's bootstrap) instead
 * of from anything persisted where a script could reach it.
 */
const ACCESS_TOKEN_KEY = 'chemelex.accessToken';

// Written by the pre-auth build (and by the removed demo-login bypass). Nothing
// reads them any more, so they are inert — but they still look like a live
// session to anyone inspecting storage, and the demo entries were fabricated
// credentials. Cleared whenever we establish or tear down a real session.
const LEGACY_LOCAL_KEYS = ['tokens'];
const LEGACY_SESSION_KEYS = ['tokens', 'accessToken', 'role'];

export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY) || null;

export const setAccessToken = (token) => {
  if (token) sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  else sessionStorage.removeItem(ACCESS_TOKEN_KEY);
};

export const clearAccessToken = () => sessionStorage.removeItem(ACCESS_TOKEN_KEY);

export const purgeLegacyTokenStores = () => {
  LEGACY_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
  LEGACY_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

/**
 * Broadcast for "the refresh token is gone or rejected" — raised by the axios
 * interceptor, consumed by AuthProvider.
 *
 * The interceptor lives outside React and can only clear storage; clearing
 * storage alone leaves `isLoggedIn: true` in context, so ProtectedRoute would
 * keep rendering the cockpit while every request 401s. This is the wire that
 * turns an expired session into an actual redirect.
 */
const authFailureListeners = new Set();

export const onAuthFailure = (listener) => {
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
};

export const emitAuthFailure = () => {
  authFailureListeners.forEach((listener) => listener());
};
